import { Injectable, NgZone, ɵgetDirectives as getDirectives, inject } from '@angular/core';
import { NgControl } from '@angular/forms';
import { MatSelect } from '@angular/material/select';

@Injectable({ providedIn: 'root' })
export class AutoPlaceholderService {
  private zone = inject(NgZone);

  init(): void {
    if (typeof document === 'undefined') return;

    this.zone.run(() => this.processNode(document.body));

    new MutationObserver((mutations) => {
      const nodes: Element[] = [];
      for (const m of mutations) {
        m.addedNodes.forEach((node) => {
          if (node instanceof Element) nodes.push(node);
        });
      }
      if (nodes.length) {
        // Defer one microtask so Angular finishes attaching directives before we read valueAccessor
        Promise.resolve().then(() => {
          this.zone.run(() => nodes.forEach((node) => this.processNode(node)));
        });
      }
    }).observe(document.body, { childList: true, subtree: true });
  }

  private processNode(root: Element): void {
    if (root.matches('input[matInput], textarea[matInput]')) {
      this.applyToInput(root as HTMLInputElement | HTMLTextAreaElement);
      return;
    }
    if (root.matches('mat-select')) {
      this.applyToSelect(root);
      return;
    }
    root.querySelectorAll<HTMLInputElement | HTMLTextAreaElement>('input[matInput], textarea[matInput]').forEach((input) => this.applyToInput(input));
    root.querySelectorAll('mat-select').forEach((sel) => this.applyToSelect(sel));
  }

  private applyToInput(input: HTMLInputElement | HTMLTextAreaElement): void {
    if (input.placeholder) return;
    if (input.hasAttribute('data-no-auto-placeholder')) return;
    const field = input.closest('mat-form-field');
    if (!field) return;
    const label = field.querySelector('mat-label');
    if (!label) return;
    const labelText = label.textContent?.trim();
    if (labelText) input.setAttribute('placeholder', labelText);
  }

  private applyToSelect(selectEl: Element): void {
    const field = selectEl.closest('mat-form-field');
    if (!field) return;
    const labelText = this.getLabelText(field);
    if (!labelText) return;

    const ngControl = getDirectives(selectEl).find((d): d is NgControl => d instanceof NgControl);
    const instance = ngControl?.valueAccessor as MatSelect | null | undefined;
    if (!instance || !(instance instanceof MatSelect) || instance.placeholder) return;

    instance.placeholder = labelText;
    (instance as any)._changeDetectorRef?.markForCheck();
  }

  private getLabelText(field: Element): string {
    return field.querySelector('mat-label')?.textContent?.trim() ?? '';
  }
}
